import { ChevronRightIcon } from "@heroicons/react/24/outline";
import React from "react";
import { Link } from "react-router-dom";

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

interface PageHeaderProps {
  breadcrumbs: BreadcrumbItem[];
  title: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
}

const Breadcrumbs: React.FC<{ items: BreadcrumbItem[] }> = ({ items }) => (
  <nav aria-label="Breadcrumb" className="flex items-center gap-2">
    {items.map((item, index) => {
      const isLast = index === items.length - 1;
      const textClass = isLast ? "text-sm font-medium text-limestone-900" : "text-sm text-limestone-600";

      return (
        <React.Fragment key={`${item.href ?? ""}-${item.label}`}>
          {item.href && !isLast ? (
            <Link to={item.href} className={textClass}>
              {item.label}
            </Link>
          ) : (
            <span className={textClass}>{item.label}</span>
          )}
          {!isLast && <ChevronRightIcon className="h-3 w-3 text-limestone-400" aria-hidden="true" />}
        </React.Fragment>
      );
    })}
  </nav>
);

// SidebarLayout (Mosaic) wraps page content in `px-6` (outer main) +
// `px-4 sm:px-6 lg:px-8` (inner div). These negative margins break the
// header out of those wrappers so it spans edge-to-edge of the main area.
const FULL_BLEED_MARGIN = "-mx-10 sm:-mx-12 lg:-mx-14";

export const PageHeader: React.FC<PageHeaderProps> = ({ breadcrumbs, title, meta, actions }) => (
  <div
    className={`flex w-auto items-end justify-between border-b border-limestone-100 bg-white pb-2 pl-6 pr-4 pt-3 ${FULL_BLEED_MARGIN}`}
  >
    <div className="flex min-w-0 flex-1 items-center">
      <div className="flex flex-col gap-1.5 py-2">
        <Breadcrumbs items={breadcrumbs} />
        <div className="flex items-center gap-4">
          <h1 className="font-display text-desktop-600 font-normal leading-8 text-limestone-900">{title}</h1>
          {meta}
        </div>
      </div>
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

// Match container for tab strip below PageHeader. Caller composes their
// TabNavigation inside.
export const PageHeaderTabsContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={`border-b border-limestone-100 bg-white px-6 ${FULL_BLEED_MARGIN}`}>{children}</div>
);
